import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { getApiBase, setApiBase, clearApiBaseOverride } from '../api/http';
import { useToasts } from '../hooks/useToasts';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { showError, showSuccess } = useToasts();

  const [apiBase, setApiBaseState] = useState(getApiBase() || '');
  const [showApiSettings, setShowApiSettings] = useState(false);

  const isMobile = (() => {
    try {
      return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    } catch (e) {
      return false;
    }
  })();

  let from = location.state?.from?.pathname || (isMobile ? '/m' : '/');
  // 移动端从根路径进入时，登录后默认进入移动端首页
  if (isMobile && (from === '/' || from === '')) from = '/m';
  // 避免从登录页本身回跳导致循环
  if (from === '/login') from = isMobile ? '/m' : '/';

  const onSubmit = async (e) => {
    e.preventDefault();          // 阻止默认 GET /login
    if (loading) return;
    setLoading(true);
    try {
      const res = await api.post('/login', {
        username,
        password
      });
      const { token, user } = res.data;
      if (token && user) {
        auth.login(user, token);
        showSuccess('登录成功，正在跳转...');
        navigate(from, { replace: true });
      } else {
        showError('账号或密码错误');
      }
    } catch (err) {
      console.error('Login request failed:', err);
      showError('账号或密码错误');
    } finally {
      setLoading(false);
    }
  };

  const onSaveApiBase = () => {
    try {
      const normalizeApiBase = (raw) => {
        const v = String(raw || '').trim();
        if (!v) return '';

        // relative path (/api or /hrms/api)
        if (v.startsWith('/')) {
          return v.replace(/\/+$/, '');
        }

        // absolute url (accept inputs like "1.2.3.4:3003" without protocol)
        const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(v);
        const guessProtocol = (() => {
          try {
            return (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:') ? 'https:' : 'http:';
          } catch (e) {
            return 'http:';
          }
        })();
        const urlLike = hasScheme ? v : `${guessProtocol}//${v}`;

        let u;
        try {
          u = new URL(urlLike);
        } catch (e) {
          return '';
        }

        const pathname = (u.pathname || '').replace(/\/+$/, '');
        const hasApiPath = pathname === '/api' || pathname.endsWith('/api') || pathname === '/hrms/api' || pathname.endsWith('/hrms/api');
        if (!pathname || pathname === '') {
          u.pathname = '/api';
        } else if (pathname === '/') {
          u.pathname = '/api';
        } else if (!hasApiPath) {
          u.pathname = (pathname || '') + '/api';
        } else {
          u.pathname = pathname;
        }

        // Keep search/hash if provided
        return u.toString().replace(/\/+$/, '');
      };

      const v = normalizeApiBase(apiBase);
      if (!v) {
        showError('请输入有效的服务器地址');
        return;
      }
      setApiBase(v);
      setApiBaseState(getApiBase() || v);
      showSuccess('服务器地址已保存');
    } catch (err) {
      console.error('save api base failed', err);
      showError('保存失败');
    }
  };

  const onResetApiBase = () => {
    try {
      clearApiBaseOverride();
      setApiBaseState(getApiBase() || '');
      showSuccess('已恢复默认地址');
    } catch (err) {
      console.error('reset api base failed', err);
      showError('重置失败');
    }
  };

  return (
    <div style={{ maxWidth: 400, margin: '100px auto', padding: 24, border: '1px solid #eee', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <h2 style={{ textAlign: 'center', marginBottom: 24 }}>员工管理系统登录</h2>

      <form onSubmit={onSubmit}>
        <div style={{ marginBottom: 16 }}>
          <input
            type="text"
            placeholder="用户名"
            value={username}
            onChange={e => setUsername(e.target.value)}
            required
            style={{ width: '100%', padding: '10px', boxSizing: 'border-box', borderRadius: 4, border: '1px solid #ccc' }}
          />
        </div>
        <div style={{ marginBottom: 24 }}>
          <input
            type="password"
            placeholder="密码"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            style={{ width: '100%', padding: '10px', boxSizing: 'border-box', borderRadius: 4, border: '1px solid #ccc' }}
          />
        </div>
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <button
            type="button"
            onClick={() => setShowApiSettings(v => !v)}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#1677ff',
              cursor: 'pointer',
              padding: 0,
              fontSize: 13,
            }}
          >
            {showApiSettings ? '收起服务器设置' : '服务器设置（高级）'}
          </button>
        </div>

        {showApiSettings && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
              当前地址：{String(getApiBase() || '')}
            </div>
            <div style={{ marginBottom: 12 }}>
              <input
                type="text"
                placeholder="后端地址，例如 http://192.168.1.2:3003 或 /hrms/api"
                value={apiBase}
                onChange={e => setApiBaseState(e.target.value)}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px',
                  boxSizing: 'border-box',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                  background: '#fff',
                  color: '#000',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={onSaveApiBase}
                style={{ flex: 1, padding: '8px', borderRadius: 4, border: '1px solid #1890ff', background: '#1890ff', color: '#fff', cursor: 'pointer' }}
              >保存并使用</button>
              <button
                type="button"
                onClick={onResetApiBase}
                style={{ flex: 1, padding: '8px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', color: '#000', cursor: 'pointer' }}
              >恢复默认</button>
            </div>
          </div>
        )}
        <button 
          type="submit" 
          style={{ 
            width: '100%', 
            padding: '12px', 
            border: 'none', 
            borderRadius: 4, 
            background: '#1890ff', 
            color: 'white', 
            fontSize: 16, 
            cursor: 'pointer' 
          }}
          disabled={loading}
        >
          {loading ? '登录中...' : '登录'}
        </button>
      </form>

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <button
          type="button"
          onClick={() => navigate('/feedback')}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#1677ff',
            cursor: 'pointer',
            padding: 0,
            fontSize: 13,
          }}
        >
          意见反馈
        </button>
      </div>
    </div>
  );
}

export default Login;

